import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import PageTourButton from './PageTourButton';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Bug, Calendar, CheckCircle2, Clock, Plus, Trash2, AlertTriangle, TrendingUp, ChevronDown, ChevronUp, Activity, Scan, Calculator, X, Mail, Bell, Send, Settings, Check, Shield, RefreshCw, Sparkles, ArrowRight, MapPin } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LineChart, Line, Legend, ReferenceLine } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import { sendOrQueue, queuedToast, listQueue } from '../utils/offlineStore';
import { buildMitePDF } from '../utils/mitePdf';
import { QueenReminderSettings } from './queen/QueenReminderSettings';

const API = process.env.REACT_APP_BACKEND_URL;

const formatTreatmentName = (name) => {
  if (!name) return '';
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-800',
  active: 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

// Hive Popup Component for showing associated hives
const HivePopup = ({ isOpen, onClose, title, hives, icon: Icon, iconColor, description, appLogo, onStartTreatment, activePlans = [] }) => {
  // Check if a hive has an active treatment by hive_id
  const getHiveTreatment = (hiveId) => {
    if (!hiveId) return null;
    return activePlans.find(p => 
      (p.status === 'active' || p.status === 'scheduled') && 
      (p.hive_ids || []).includes(hiveId)
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {Icon && <Icon className={`h-5 w-5 ${iconColor}`} />}
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="mt-4">
          {hives && hives.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {hives.map((hive, idx) => {
                const hiveNum = hive.hive_number || hive.number || hive;
                const hiveId = hive.hive_id;
                const existingTreatment = getHiveTreatment(hiveId);
                return (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200 hover:bg-stone-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center overflow-hidden">
                      {appLogo ? (
                        <img 
                          src={`${API}/api/uploads/${appLogo}`} 
                          alt="App Logo" 
                          className="w-8 h-8 object-contain"
                        />
                      ) : (
                        <span className="text-amber-700 font-bold text-lg">🐝</span>
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-stone-800">
                        Hive {hiveNum}
                      </div>
                      {hive.yard_name && (
                        <div data-testid={`popup-hive-apiary-${idx}`} className="text-xs text-teal-700 flex items-center gap-1"><MapPin className="h-3 w-3" />{hive.yard_name}</div>
                      )}
                      {hive.date && (
                        <div className="text-xs text-stone-400">{new Date(hive.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                      )}
                      {hive.percentage !== undefined && (
                        <div className={`text-xs font-medium ${hive.percentage >= 3 ? 'text-red-600' : 'text-green-600'}`}>
                          Mite Load: {hive.percentage.toFixed(1)}%
                        </div>
                      )}
                      {hive.treatment_name && (
                        <div className="text-xs text-amber-600">{hive.treatment_name}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {hive.status && (
                      <Badge className={STATUS_COLORS[hive.status] || 'bg-gray-100'}>
                        {hive.status}
                      </Badge>
                    )}
                    {onStartTreatment && (
                      existingTreatment ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                          <Check className="h-3 w-3" />
                          {formatTreatmentName(existingTreatment.treatment_type) || 'Treatment'} started
                        </span>
                      ) : (
                        <button
                          onClick={() => { onClose(); onStartTreatment(hiveNum, hive.hive_id); }}
                          className="text-xs bg-red-100 text-red-700 hover:bg-red-200 px-3 py-1.5 rounded-full font-medium transition-colors"
                          data-testid={`start-treatment-hive-${hiveNum}`}
                        >
                          Start Treatment
                        </button>
                      )
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-stone-500">
              <Bug className="h-12 w-12 mx-auto mb-3 text-stone-300" />
              <p>No hives to display</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TreatmentPlansView = ({ token, hives, yards, onAddPlan, onRefresh, refreshKey, onOpenFrameScan, onOpenMiteCount, appSettings }) => {
  const [plans, setPlans] = useState([]);
  const [activeSummary, setActiveSummary] = useState(null);
  const [miteCountSummary, setMiteCountSummary] = useState(null);
  const [miteCountHistory, setMiteCountHistory] = useState([]);
  const [efficacyReport, setEfficacyReport] = useState(null);
  const [seasonalRecs, setSeasonalRecs] = useState(null);
  const [rotationData, setRotationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [yardFilter, setYardFilter] = useState('all');
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [showEfficacyReport, setShowEfficacyReport] = useState(false);
  const [showSeasonalDetails, setShowSeasonalDetails] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [showMiteCountDialog, setShowMiteCountDialog] = useState(false);
  const [miteCountTask, setMiteCountTask] = useState(null); // {planId, taskId}
  const [postMiteCount, setPostMiteCount] = useState('');
  const [postBeeSample, setPostBeeSample] = useState('300');

  const exportMiteCSV = () => {
    try {
      let csv = "data:text/csv;charset=utf-8,";
      csv += "MITE CONTROL REPORT\n";
      csv += `Generated,${new Date().toLocaleDateString()}\n`;
      csv += `Apiary,${yardFilter === 'all' ? 'All Apiaries' : yardFilter}\n\n`;
      
      // Summary
      if (miteSummary) {
        csv += "MITE COUNT SUMMARY\n";
        csv += `Total Hives Monitored,${miteSummary.total_hives || 0}\n`;
        csv += `Average Mite Load,${(miteSummary.average_percentage || 0).toFixed(1)}%\n`;
        csv += `High Risk Hives,${(miteSummary.high_risk_hives || []).length}\n\n`;
        
        if (miteSummary.high_risk_hives?.length) {
          csv += "HIGH RISK HIVES\n";
          csv += "Hive,Mite Load %,Date\n";
          miteSummary.high_risk_hives.forEach(h => {
            csv += `Hive ${h.hive_number},${(h.percentage || 0).toFixed(1)}%,${h.date || ''}\n`;
          });
          csv += "\n";
        }
      }
      
      // Treatment Plans
      csv += "TREATMENT PLANS\n";
      csv += "Status,Treatment,Apiary,Hives,Start Date,End Date,Notes\n";
      yardPlans.forEach(p => {
        const hiveNums = (p.hive_ids || []).map(hid => {
          const h = hives.find(hv => hv.id === hid);
          return h ? (h.nickname || h.hive_number) : '?';
        }).join('; ');
        const planYard = p.yard_name || [...new Set((p.hive_ids || []).map(hid => hives.find(hv => hv.id === hid)?.yard).filter(Boolean))].join('; ');
        csv += `${p.status || ''},${p.treatment_type_name || p.treatment_type || ''},"${planYard}",${hiveNums},${p.start_date || ''},${p.end_date || ''},"${(p.notes || '').replace(/"/g, '""')}"\n`;
      });
      csv += "\n";
      
      // Mite Count History
      if (yardMiteHistory?.length) {
        csv += "MITE COUNT HISTORY\n";
        csv += "Date,Hive,Method,Count,Bees Sampled,Percentage\n";
        yardMiteHistory.forEach(m => {
          csv += `${m.date || ''},Hive ${m.hive_number || '?'},${m.method || ''},${m.mite_count || 0},${m.bee_count || 300},${(m.percentage || 0).toFixed(1)}%\n`;
        });
      }
      
      const link = document.createElement("a");
      link.href = encodeURI(csv);
      link.download = `mite_control_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('CSV exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const exportMitePDF = () => {
    try {
      const doc = buildMitePDF({
        appName: appSettings?.app_name,
        apiaryLabel: yardFilter === 'all' ? 'All Apiaries' : yardFilter,
        summary: miteSummary,
        activeCount: yardActiveSummary?.active_count || 0,
        history: yardMiteHistory,
        plans: yardPlans,
        hives: hives || [],
        efficacy: efficacyReport?.report || []
      });
      doc.save(`mite_control_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };
  
  // Popup states
  const [showActiveTreatmentPopup, setShowActiveTreatmentPopup] = useState(false);
  const [showMiteLoadPopup, setShowMiteLoadPopup] = useState(false);
  const [showHighRiskPopup, setShowHighRiskPopup] = useState(false);

  useEffect(() => {
    fetchData();
  }, [refreshKey]);

  useEffect(() => {
    const onRefresh = () => fetchData();
    window.addEventListener('refreshData', onRefresh);
    return () => window.removeEventListener('refreshData', onRefresh);
  }, []);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = yardFilter === 'all' ? {} : { yard: yardFilter };
    axios.get(`${API}/api/treatment-plans/efficacy-report`, { headers, params })
      .then(res => setEfficacyReport(res.data))
      .catch(() => setEfficacyReport({ report: [] }));
  }, [yardFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [plansRes, activeRes, miteRes, miteHistoryRes, efficacyRes, seasonalRes, notifRes, rotationRes] = await Promise.all([
        axios.get(`${API}/api/treatment-plans`, { headers }),
        axios.get(`${API}/api/treatment-plans/active`, { headers }),
        axios.get(`${API}/api/mite-counts/summary`, { headers }),
        axios.get(`${API}/api/mite-counts`, { headers }),
        axios.get(`${API}/api/treatment-plans/efficacy-report`, { headers, params: yardFilter === 'all' ? {} : { yard: yardFilter } }).catch(() => ({ data: { report: [] } })),
        axios.get(`${API}/api/treatment-plans/seasonal-recommendations`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/api/notification-settings`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/api/treatment-plans/rotation-analysis`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setPlans(overlayQueued(plansRes.data.plans || [], await listQueue().catch(() => [])));
      setActiveSummary(activeRes.data);
      setMiteCountSummary(miteRes.data);
      setMiteCountHistory(miteHistoryRes.data.counts || []);
      setEfficacyReport(efficacyRes.data);
      setSeasonalRecs(seasonalRes.data);
      setNotificationSettings(notifRes.data);
      setRotationData(rotationRes.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async () => {
    setSendingReminder(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/api/treatment-plans/send-seasonal-reminder`, {}, { headers });
      toast.success(response.data.message || 'Reminder sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const handleUpdateNotificationSettings = async (key, value) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/api/notification-settings`, { [key]: value }, { headers });
      setNotificationSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Settings updated');
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  // Re-apply offline changes still waiting in the queue on top of the (cached) plans
  const overlayQueued = (list, queue) => queue.reduce((acc, item) => {
    const steps = [{ method: item.method, path: item.path, body: item.body }, ...(item.followUps || [])];
    return steps.reduce((ps, st) => {
      const m = (st.path || '').match(/^\/treatment-plans\/([^/]+)(?:\/tasks\/([^/]+))?$/);
      if (!m || st.method !== 'put') return ps;
      const [, planId, taskId] = m;
      return ps.map(p => p.id !== planId ? p : {
        ...p, ...(taskId ? {} : st.body), pending_sync: true,
        tasks: taskId ? (p.tasks || []).map(t => t.id === taskId ? { ...t, ...st.body } : t) : p.tasks,
      });
    }, acc);
  }, list);

  // Show queued offline changes straight away so they can't be tapped twice
  const markLocal = (planId, { taskId, taskStatus, ...planFields }) => setPlans(prev => prev.map(p => p.id !== planId ? p : {
    ...p, ...planFields, pending_sync: true,
    tasks: taskId ? (p.tasks || []).map(t => t.id === taskId ? { ...t, status: taskStatus } : t) : p.tasks,
  }));

  const handleTaskComplete = async (planId, taskId, taskType) => {
    // If it's a mite count task, show the mite count dialog first
    if (taskType === 'mite_count') {
      setMiteCountTask({ planId, taskId });
      setPostMiteCount('');
      setPostBeeSample('300');
      setShowMiteCountDialog(true);
      return;
    }
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { queued } = await sendOrQueue({ method: 'put', path: `/treatment-plans/${planId}/tasks/${taskId}`, body: { status: 'completed', completed_at: new Date().toISOString() }, kind: 'treatment_dose', label: 'Treatment task done' });
      if (queued) { queuedToast('treatment task'); markLocal(planId, { taskId, taskStatus: 'completed' }); return; } else toast.success('Task marked complete');
      fetchData();
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  const handleMiteCountSubmit = async () => {
    if (!miteCountTask || !postMiteCount) return;
    const { planId, taskId, completePlan } = miteCountTask;
    const miteNum = parseInt(postMiteCount);
    const beeNum = parseInt(postBeeSample) || 300;
    const percentage = parseFloat(((miteNum / beeNum) * 100).toFixed(1));
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Save the post-treatment mite count on the plan
      const planUpdate = { 
        post_treatment_mite_count: miteNum, 
        post_treatment_bee_sample: beeNum, 
        post_treatment_mite_percentage: percentage 
      };
      // If triggered from "Mark Done" on plan, also complete the plan
      if (completePlan) {
        planUpdate.status = 'completed';
      }
      const { queued } = await sendOrQueue({ method: 'put', path: `/treatment-plans/${planId}/tasks/${taskId}`, body: { status: 'completed', completed_at: new Date().toISOString() },
        kind: 'treatment_dose', label: `Post-treatment mite count · ${miteNum} mites`, followUps: [{ method: 'put', path: `/treatment-plans/${planId}`, body: planUpdate }] });
      if (queued) { queuedToast('mite count'); markLocal(planId, { taskId, taskStatus: 'completed', ...planUpdate }); setShowMiteCountDialog(false); setMiteCountTask(null); return; } else toast.success(`Post-treatment mite count recorded: ${miteNum} mites (${percentage}%)`);
      setShowMiteCountDialog(false);
      setMiteCountTask(null);
      fetchData();
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to save mite count');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm('Delete this treatment plan? This cannot be undone.')) return;
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/api/treatment-plans/${planId}`, { headers });
      toast.success('Plan deleted');
      fetchData();
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to delete plan');
    }
  };

  const handleCompletePlan = async (planId) => {
    // Check if there's an incomplete mite_count task - prompt for count first
    const plan = plans.find(p => p.id === planId);
    const pendingMiteTask = plan?.tasks?.find(t => t.type === 'mite_count' && t.status !== 'completed');
    if (pendingMiteTask) {
      setMiteCountTask({ planId, taskId: pendingMiteTask.id, completePlan: true });
      setPostMiteCount('');
      setPostBeeSample('300');
      setShowMiteCountDialog(true);
      return;
    }
    
    if (!confirm('Mark this treatment plan as complete?')) return;
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { queued } = await sendOrQueue({ method: 'put', path: `/treatment-plans/${planId}`, body: { status: 'completed' }, kind: 'treatment_dose', label: 'Treatment plan complete' });
      if (queued) { queuedToast('plan completion'); markLocal(planId, { status: 'completed' }); return; } else toast.success('Treatment plan marked as complete');
      fetchData();
      onRefresh?.();
    } catch (err) {
      toast.error('Failed to update plan');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Ongoing';
    return new Date(dateStr).toLocaleDateString('en-AU', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  const yardHiveIds = new Set((hives || []).filter(h => h.yard === yardFilter).map(h => h.id));
  const yardHiveNums = new Set((hives || []).filter(h => h.yard === yardFilter).map(h => String(h.hive_number)));
  const inYard = (p) => yardFilter === 'all' || p.yard_name === yardFilter || (p.hive_ids || []).some(id => yardHiveIds.has(id));
  const countInYard = (c) => yardFilter === 'all' || yardHiveIds.has(c.hive_id) || yardHiveNums.has(String(c.hive_number));
  const yardPlans = plans.filter(inYard);
  const yardMiteHistory = miteCountHistory.filter(countInYard);
  const yardOfHive = (id, num) => ((hives || []).find(h => h.id === id) || (hives || []).find(h => String(h.hive_number) === String(num)))?.yard || '';
  const miteSummary = (() => {
    if (yardFilter === 'all') return miteCountSummary;
    const total = yardMiteHistory.length;
    const latestByHive = {};
    yardMiteHistory.forEach(c => { if (c.hive_id && !latestByHive[c.hive_id]) latestByHive[c.hive_id] = c; });
    return {
      total_counts: total,
      total_hives: Object.keys(latestByHive).length,
      average_percentage: total ? Math.round(yardMiteHistory.reduce((sum, c) => sum + (c.percentage || 0), 0) / total * 100) / 100 : 0,
      latest: yardMiteHistory[0] || null,
      high_risk_hives: Object.values(latestByHive).filter(c => (c.percentage || 0) >= 3)
    };
  })();
  const activePlans = (activeSummary?.plans || []).filter(inYard);
  const yardActiveSummary = yardFilter === 'all' ? activeSummary : (activeSummary && {
    ...activeSummary,
    plans: activePlans,
    active_count: activePlans.filter(p => p.status === 'active').length,
    scheduled_count: activePlans.filter(p => p.status === 'scheduled').length
  });
  const filteredPlans = (statusFilter === 'all' 
    ? yardPlans 
    : yardPlans.filter(p => p.status === statusFilter)
  ).sort((a, b) => (b.start_date || b.created_at || '').localeCompare(a.start_date || a.created_at || ''));

  // Prepare chart data
  const APIARY_COLORS = ['#d97706', '#0d9488', '#7c3aed', '#2563eb', '#db2777', '#65a30d', '#ea580c', '#0891b2'];
  const chartCounts = yardMiteHistory.slice(0, 60).reverse();
  const chartApiaries = [...new Set(chartCounts.map(c => yardOfHive(c.hive_id, c.hive_number) || 'Unassigned'))];
  const apiaryColor = (a) => {
    const idx = (yards || []).findIndex(y => y.name === a);
    return a === 'Unassigned' || idx < 0 ? '#78716c' : APIARY_COLORS[idx % APIARY_COLORS.length];
  };
  const miteChartData = Object.values(chartCounts.reduce((acc, c) => {
    const key = (c.date || '').slice(0, 10);
    const apiary = yardOfHive(c.hive_id, c.hive_number) || 'Unassigned';
    acc[key] = acc[key] || { date: new Date(c.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }), _sums: {} };
    const sums = acc[key]._sums;
    sums[apiary] = sums[apiary] || [0, 0];
    sums[apiary][0] += c.percentage || 0;
    sums[apiary][1] += 1;
    return acc;
  }, {})).map(row => {
    const out = { date: row.date };
    Object.entries(row._sums).forEach(([a, [sum, n]]) => { out[a] = Math.round((sum / n) * 10) / 10; });
    return out;
  });

  return (
    <div className="space-y-6">
      {/* Page Heading */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Bug className="h-6 w-6 text-amber-600" />
            Mite Control
          </h1>
          <p className="text-sm text-stone-500 mt-1">Track and manage varroa treatments across your hives</p>
        </div>
        <div className="flex items-center gap-2">
          {(yards || []).length > 0 && (
            <div className="flex items-center gap-2" data-testid="mite-yard-filter">
              <MapPin className="h-4 w-4 text-stone-400" />
              <Select value={yardFilter} onValueChange={setYardFilter}>
                <SelectTrigger data-testid="mite-yard-filter-select" className="w-[180px] h-9 text-sm">
                  <SelectValue placeholder="All Apiaries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Apiaries</SelectItem>
                  {yards.map(y => <SelectItem key={y.id} value={y.name}>{y.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <PageTourButton pageView="treatments" />
        </div>
      </div>

      {yardFilter !== 'all' && (
        <p data-testid="mite-filter-caption" className="text-sm text-teal-700 flex items-center gap-1 -mb-3"><MapPin className="h-4 w-4" /> Showing {yardFilter} only</p>
      )}
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Active Treatments - Clickable */}
        <div 
          className="bg-gradient-to-br from-amber-50 to-orange-100 border border-amber-200 rounded-xl p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setShowActiveTreatmentPopup(true)}
          data-testid="active-treatments-card"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-amber-800">Active Treatments</h3>
            <Activity className="h-5 w-5 text-amber-600" />
          </div>
          <div className="text-3xl font-bold text-amber-900">
            {yardActiveSummary?.active_count || 0}
          </div>
          {yardActiveSummary?.plans?.filter(p => p.status === 'active').length > 0 && (
            <div className="mt-2 space-y-1">
              {yardActiveSummary.plans.filter(p => p.status === 'active').map(p => (
                <p key={p.id} className="text-xs text-amber-700 flex items-center gap-1">
                  <span className="font-medium">{formatTreatmentName(p.treatment_type_name || p.treatment_type) || 'Treatment'}</span>
                  <span className="text-amber-500">— {(p.hive_ids || []).length} hive{(p.hive_ids || []).length !== 1 ? 's' : ''}</span>
                </p>
              ))}
            </div>
          )}
          <p className="text-sm text-amber-700 mt-1">
            {yardActiveSummary?.scheduled_count || 0} scheduled
          </p>
          <p className="text-xs text-amber-600 mt-2">Click to view hives →</p>
        </div>

        {/* Mite Load Average - Clickable */}
        <div 
          className={`border rounded-xl p-5 cursor-pointer hover:shadow-md transition-shadow ${
            (miteSummary?.average_percentage || 0) >= 3 
              ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-200' 
              : 'bg-gradient-to-br from-green-50 to-emerald-100 border-green-200'
          }`}
          onClick={() => setShowMiteLoadPopup(true)}
          data-testid="avg-mite-load-card"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className={`font-semibold ${(miteSummary?.average_percentage || 0) >= 3 ? 'text-red-800' : 'text-green-800'}`}>
              Avg Mite Load
            </h3>
            <Bug className={`h-5 w-5 ${(miteSummary?.average_percentage || 0) >= 3 ? 'text-red-600' : 'text-green-600'}`} />
          </div>
          <div className={`text-3xl font-bold ${(miteSummary?.average_percentage || 0) >= 3 ? 'text-red-900' : 'text-green-900'}`}>
            {miteSummary?.average_percentage?.toFixed(1) || 0}%
          </div>
          <p className={`text-sm mt-1 ${(miteSummary?.average_percentage || 0) >= 3 ? 'text-red-700' : 'text-green-700'}`}>
            {(miteSummary?.average_percentage || 0) >= 3 ? 'Treatment recommended' : 'Within safe levels'}
          </p>
          <p className={`text-xs mt-2 ${(miteSummary?.average_percentage || 0) >= 3 ? 'text-red-600' : 'text-green-600'}`}>Click to view tested hives →</p>
        </div>

        {/* High Risk Hives - Clickable */}
        <div 
          className="bg-gradient-to-br from-red-50 to-rose-100 border border-red-200 rounded-xl p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setShowHighRiskPopup(true)}
          data-testid="high-risk-hives-card"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-red-800">High Risk Hives</h3>
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div className="text-3xl font-bold text-red-900">
            {miteSummary?.high_risk_hives?.length || 0}
          </div>
          <p className="text-sm text-red-700 mt-1">
            Above 3% threshold
          </p>
          <p className="text-xs text-red-600 mt-2">Click to view hives →</p>
        </div>

        {/* Quick Actions */}
        <div 
          className="bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200 rounded-xl p-5 cursor-pointer hover:shadow-md transition-shadow"
          onClick={onAddPlan}
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-blue-800">New Treatment</h3>
            <Plus className="h-5 w-5 text-blue-600" />
          </div>
          <div className="text-lg font-medium text-blue-700">
            Start Treatment Wizard
          </div>
          <p className="text-sm text-blue-600 mt-1">
            Create a plan with auto-scheduling
          </p>
        </div>
      </div>

      {/* Quick Action Buttons */}
      <div className="flex flex-wrap gap-3">
        {onOpenFrameScan && (
          <Button
            onClick={onOpenFrameScan}
            variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            <Scan className="h-4 w-4 mr-2" />
            AI Frame Scanner
          </Button>
        )}
        {onOpenMiteCount && (
          <Button
            onClick={onOpenMiteCount}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <Calculator className="h-4 w-4 mr-2" />
            Record Mite Count
          </Button>
        )}
      </div>

      {/* Mite Count Chart */}
      {miteChartData.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-amber-600" />
            Mite Trend by Apiary
          </h3>
          <div className="h-64" data-testid="mite-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={miteChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, (max) => Math.max(4, Math.ceil(max))]} />
                <Tooltip 
                  formatter={(v, name) => [`${v}%`, name]}
                  contentStyle={{ borderRadius: '8px' }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {chartApiaries.map((a) => (
                  <Line
                    key={a}
                    type="monotone"
                    dataKey={a}
                    name={a}
                    stroke={apiaryColor(a)}
                    strokeWidth={2}
                    connectNulls
                    dot={{ fill: apiaryColor(a), r: 4 }}
                  />
                ))}
                <ReferenceLine y={3} stroke="#ef4444" strokeDasharray="5 5" label={{ value: '3%', position: 'insideTopRight', fontSize: 10, fill: '#ef4444' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-center text-gray-500 mt-2">
            Average mite load per apiary on each test date · Red dashed line = 3% treatment threshold
          </p>
        </div>
      )}

      {yardFilter !== 'all' && !(efficacyReport?.report?.length > 0) && (
        <div data-testid="efficacy-empty-yard" className="bg-white rounded-xl border border-stone-200 p-5 text-sm text-stone-500">
          No completed treatments with before/after mite counts in {yardFilter} yet, so there's no efficacy data to compare.
        </div>
      )}
      {/* Treatment Efficacy Comparison Report */}
      {efficacyReport && efficacyReport.report && efficacyReport.report.length > 0 && (
        <div className="bg-white rounded-xl border border-stone-200 p-5" data-testid="efficacy-report-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-purple-600" />
              Treatment Efficacy Comparison
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEfficacyReport(!showEfficacyReport)}
              className="text-xs"
            >
              {showEfficacyReport ? 'Hide Details' : 'Show Details'}
              {showEfficacyReport ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
            </Button>
          </div>
          
          {/* Bar Chart Comparison */}
          <div className="h-52 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={efficacyReport.report}
                layout="vertical"
                margin={{ left: 20, right: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} />
                <YAxis 
                  type="category" 
                  dataKey="treatment_type" 
                  tick={{ fontSize: 10 }} 
                  width={120}
                  tickFormatter={(v) => v.length > 18 ? v.slice(0, 18) + '...' : v}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'avg_reduction') return [`${value}%`, 'Avg Reduction'];
                    if (name === 'success_rate') return [`${value}%`, 'Success Rate (≥80%)'];
                    return [value, name];
                  }}
                  contentStyle={{ borderRadius: '8px', fontSize: '12px' }}
                />
                <Bar dataKey="avg_reduction" name="avg_reduction" radius={[0, 4, 4, 0]}>
                  {efficacyReport.report.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.avg_reduction >= 80 ? '#22c55e' : entry.avg_reduction >= 60 ? '#f59e0b' : '#ef4444'} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-purple-50 rounded-lg p-3 text-center border border-purple-100">
              <div className="text-xs text-purple-600">Total Treatments</div>
              <div className="text-xl font-bold text-purple-700">{efficacyReport.total_treatments}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center border border-green-100">
              <div className="text-xs text-green-600">Treatment Types</div>
              <div className="text-xl font-bold text-green-700">{efficacyReport.total_treatment_types}</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-100">
              <div className="text-xs text-amber-600">Best Performer</div>
              <div className="text-lg font-bold text-amber-700 truncate">
                {efficacyReport.report[0]?.treatment_type?.split(' ')[0] || '--'}
              </div>
            </div>
          </div>

          {/* Detailed Table */}
          {showEfficacyReport && (
            <div className="space-y-3 border-t border-stone-200 pt-4">
              <h4 className="font-medium text-stone-700 text-sm">Detailed Breakdown</h4>
              {efficacyReport.report.map((item, idx) => (
                <div 
                  key={idx} 
                  className={`p-4 rounded-xl border ${
                    item.avg_reduction >= 80 
                      ? 'bg-green-50 border-green-200' 
                      : item.avg_reduction >= 60 
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-bold text-stone-800">{item.treatment_type}</div>
                      <div className="text-xs text-stone-500">
                        {item.total_treatments} treatment{item.total_treatments > 1 ? 's' : ''} • {item.total_hives_treated} hive{item.total_hives_treated > 1 ? 's' : ''} treated
                      </div>
                    </div>
                    <div className={`text-2xl font-bold ${
                      item.avg_reduction >= 80 ? 'text-green-600' : item.avg_reduction >= 60 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {item.avg_reduction}%
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div className="bg-white/50 rounded p-2 text-center">
                      <div className="text-stone-500">Avg Before</div>
                      <div className="font-bold text-red-600">{item.avg_pre_mite}%</div>
                    </div>
                    <div className="bg-white/50 rounded p-2 text-center">
                      <div className="text-stone-500">Avg After</div>
                      <div className="font-bold text-green-600">{item.avg_post_mite}%</div>
                    </div>
                    <div className="bg-white/50 rounded p-2 text-center">
                      <div className="text-stone-500">Success Rate</div>
                      <div className="font-bold text-purple-600">{item.success_rate}%</div>
                    </div>
                    <div className="bg-white/50 rounded p-2 text-center">
                      <div className="text-stone-500">Range</div>
                      <div className="font-bold text-stone-700">{item.min_reduction}-{item.max_reduction}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <p className="text-xs text-center text-gray-500 mt-3">
            Green = ≥80% effective • Amber = 60-79% • Red = &lt;60%
          </p>
        </div>
      )}

      {/* Treatment Rotation Planner */}
      {rotationData && (
        <RotationPlanner data={rotationData} />
      )}

      {/* Seasonal Treatment Recommendations */}
      {seasonalRecs && (seasonalRecs.tips?.length > 0 || seasonalRecs.season_recommendations?.length > 0) && (
        <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-5" data-testid="seasonal-recs-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-indigo-600" />
              Seasonal Recommendations
              {seasonalRecs.current_season && (
                <span className="text-sm font-normal px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">
                  {seasonalRecs.current_season}
                </span>
              )}
            </h3>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNotificationSettings(true)}
                className="text-xs border-indigo-300 text-indigo-700"
                title="Notification Settings"
              >
                <Bell className="h-3 w-3" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendReminder}
                disabled={sendingReminder}
                className="text-xs border-indigo-300 text-indigo-700"
              >
                {sendingReminder ? (
                  <div className="animate-spin h-3 w-3 border-2 border-indigo-500 border-t-transparent rounded-full" />
                ) : (
                  <Mail className="h-3 w-3 mr-1" />
                )}
                {sendingReminder ? 'Sending...' : 'Email Reminder'}
              </Button>
              {seasonalRecs.season_recommendations?.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSeasonalDetails(!showSeasonalDetails)}
                  className="text-xs border-indigo-300 text-indigo-700"
                >
                  {showSeasonalDetails ? 'Hide' : 'Details'}
                  {showSeasonalDetails ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                </Button>
              )}
            </div>
          </div>

          {/* Current Season Recommendation */}
          {seasonalRecs.current_recommendation && (
            <div className="bg-white/70 rounded-xl p-4 border border-indigo-100 mb-4">
              <div className="flex items-start gap-3">
                <div className="text-3xl">
                  {seasonalRecs.current_season === 'Spring' ? '🌸' : 
                   seasonalRecs.current_season === 'Summer' ? '☀️' :
                   seasonalRecs.current_season === 'Autumn' ? '🍂' : '❄️'}
                </div>
                <div className="flex-1">
                  <div className="text-sm text-indigo-600 font-medium">Recommended for {seasonalRecs.current_season}</div>
                  <div className="text-xl font-bold text-stone-800">{seasonalRecs.current_recommendation.recommended_treatment}</div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                      {seasonalRecs.current_recommendation.avg_reduction}% avg reduction
                    </span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                      {seasonalRecs.current_recommendation.success_rate}% success rate
                    </span>
                    <span className="px-2 py-1 bg-stone-100 text-stone-600 text-xs rounded-full">
                      Based on {seasonalRecs.current_recommendation.treatments_analyzed} treatment{seasonalRecs.current_recommendation.treatments_analyzed > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tips */}
          {seasonalRecs.tips?.length > 0 && (
            <div className="space-y-2 mb-4">
              {seasonalRecs.tips.map((tip, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-white/50 rounded-lg">
                  <span className="text-xl">{tip.icon}</span>
                  <div>
                    <div className="font-medium text-stone-800 text-sm">{tip.title}</div>
                    <div className="text-stone-600 text-xs">{tip.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Seasonal Breakdown */}
          {showSeasonalDetails && seasonalRecs.season_recommendations?.length > 0 && (
            <div className="border-t border-indigo-100 pt-4 mt-4">
              <h4 className="font-medium text-stone-700 text-sm mb-3">Best Treatment by Season</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Spring', 'Summer', 'Autumn', 'Winter'].map(season => {
                  const rec = seasonalRecs.season_recommendations.find(r => r.season === season);
                  const seasonEmoji = season === 'Spring' ? '🌸' : season === 'Summer' ? '☀️' : season === 'Autumn' ? '🍂' : '❄️';
                  const isCurrentSeason = season === seasonalRecs.current_season;
                  
                  return (
                    <div 
                      key={season} 
                      className={`p-3 rounded-lg border text-center ${
                        isCurrentSeason 
                          ? 'bg-indigo-100 border-indigo-300' 
                          : 'bg-white/60 border-stone-200'
                      }`}
                    >
                      <div className="text-2xl mb-1">{seasonEmoji}</div>
                      <div className={`font-medium text-sm ${isCurrentSeason ? 'text-indigo-700' : 'text-stone-700'}`}>
                        {season}
                      </div>
                      {rec ? (
                        <>
                          <div className="text-xs text-stone-600 truncate mt-1" title={rec.recommended_treatment}>
                            {rec.recommended_treatment.split(' ')[0]}
                          </div>
                          <div className={`text-lg font-bold ${rec.avg_reduction >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                            {rec.avg_reduction}%
                          </div>
                        </>
                      ) : (
                        <div className="text-xs text-stone-400 mt-1">No data yet</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <p className="text-xs text-center text-indigo-400 mt-3">
            Based on {seasonalRecs.total_treatments_analyzed} completed treatment{seasonalRecs.total_treatments_analyzed !== 1 ? 's' : ''} with efficacy data
          </p>
        </div>
      )}

      {/* Treatment Plans List */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm">
        <div className="p-4 border-b border-stone-100 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Bug className="h-6 w-6 text-amber-600" />
            Treatment Plans
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" size="sm" onClick={exportMiteCSV} className="h-9 text-xs">
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={exportMitePDF} className="h-9 text-xs">
              PDF
            </Button>
            
            <Button onClick={onAddPlan} className="bg-amber-500 hover:bg-amber-600 h-9">
              <Plus className="h-4 w-4 mr-1" />
              New Plan
            </Button>
          </div>
        </div>

        <div className="divide-y divide-stone-100">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-5xl mb-4">🐛</div>
              <h3 className="text-lg font-semibold text-stone-700 mb-2">No Treatment Plans</h3>
              <p className="text-stone-500 mb-4">Create a treatment plan to protect your hives from mites.</p>
              <Button onClick={onAddPlan} className="bg-amber-500 hover:bg-amber-600">
                <Plus className="h-4 w-4 mr-2" />
                Create First Plan
              </Button>
            </div>
          ) : (
            filteredPlans.map(plan => (
              <div key={plan.id} className="p-4">
                <div 
                  className="flex items-start justify-between cursor-pointer"
                  onClick={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h4 className="font-semibold">{formatTreatmentName(plan.name || plan.treatment_type_name || plan.treatment_type) || 'Treatment Plan'}</h4>
                      <Badge className={STATUS_COLORS[plan.status]}>
                        {plan.status}
                      </Badge>
                      {plan.mite_type && (
                      <Badge variant="outline" className="capitalize">
                        {plan.mite_type}
                      </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-stone-600 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(plan.start_date)} - {formatDate(plan.end_date)}
                        {plan.pending_sync && <span className="ml-2 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" data-testid={`plan-pending-sync-${plan.id}`}>Pending sync</span>}
                      </span>
                      <span>{formatTreatmentName(plan.treatment_type_name)}</span>
                      <span className="flex items-center gap-1 flex-wrap">
                        {(plan.hive_ids || []).length > 0 ? (
                          (plan.hive_ids || []).map((hid, i) => {
                            const h = hives.find(hv => hv.id === hid);
                            const num = h ? (h.nickname || h.hive_number) : (plan.hive_numbers?.[i] || '?');
                            return <span key={hid} className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-xs font-medium">#{num}</span>;
                          })
                        ) : '0 hives'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {plan.status !== 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-300 hover:bg-green-50"
                        onClick={(e) => { e.stopPropagation(); handleCompletePlan(plan.id); }}
                        data-testid={`complete-plan-${plan.id}`}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Mark Done
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={(e) => { e.stopPropagation(); handleDeletePlan(plan.id); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    {expandedPlan === plan.id ? (
                      <ChevronUp className="h-5 w-5 text-stone-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-stone-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedPlan === plan.id && (
                  <div className="mt-4 pt-4 border-t border-stone-100">
                    {/* Notes */}
                    {plan.notes && (
                      <div className="mb-4">
                        <h5 className="text-sm font-medium text-stone-600 mb-1">Notes</h5>
                        <p className="text-sm text-stone-500 italic">"{plan.notes}"</p>
                      </div>
                    )}

                    {/* Treatment details for bulk plans */}
                    {plan.source === 'bulk_action' && (
                      <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-sm text-amber-800">
                          <strong>Bulk Treatment</strong> — {formatTreatmentName(plan.treatment_type_name || plan.treatment_type)} applied to {(plan.hive_ids || []).length} hives in {plan.yard_name || 'yard'}
                          {plan.dosage && <span> • Dosage: {plan.dosage}</span>}
                        </p>
                      </div>
                    )}

                    {/* Tasks & Schedule */}
                    {plan.tasks?.length > 0 && (
                      <>
                    <h5 className="text-sm font-medium text-stone-600 mb-3">Tasks & Schedule</h5>
                    <div className="space-y-2">
                      {plan.tasks?.map(task => (
                        <div 
                          key={task.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            task.status === 'completed' ? 'bg-green-50' : 'bg-stone-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              task.status === 'completed' 
                                ? 'bg-green-500 text-white' 
                                : task.type === 'remove' 
                                  ? 'bg-red-100 text-red-600'
                                  : 'bg-amber-100 text-amber-600'
                            }`}>
                              {task.status === 'completed' ? (
                                <CheckCircle2 className="h-4 w-4" />
                              ) : task.type === 'apply' ? (
                                <Bug className="h-4 w-4" />
                              ) : task.type === 'remove' ? (
                                <Trash2 className="h-4 w-4" />
                              ) : (
                                <Activity className="h-4 w-4" />
                              )}
                            </div>
                            <div>
                              <p className={`font-medium ${task.status === 'completed' ? 'line-through text-stone-400' : ''}`}>
                                {task.title}
                              </p>
                              <p className="text-sm text-stone-500">{formatDate(task.date)}</p>
                            </div>
                          </div>
                          {task.status !== 'completed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleTaskComplete(plan.id, task.id, task.type); }}
                              className="text-green-600 border-green-300 hover:bg-green-50"
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Done
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {plan.pre_treatment_mite_count && (
                      <p className="mt-2 text-sm text-stone-600">
                        Pre-treatment mite load: <strong>{plan.pre_treatment_mite_count}%</strong>
                      </p>
                    )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Popup Dialogs */}
      
      {/* Active Treatments Popup */}
      <HivePopup
        isOpen={showActiveTreatmentPopup}
        onClose={() => setShowActiveTreatmentPopup(false)}
        title="Hives Under Treatment"
        icon={Activity}
        iconColor="text-amber-600"
        description={`${yardActiveSummary?.active_count || 0} active, ${yardActiveSummary?.scheduled_count || 0} scheduled treatments`}
        appLogo={appSettings?.app_logo}
        hives={(yardActiveSummary?.plans || []).flatMap(plan => {
          const numbers = plan.hive_numbers || [];
          const ids = plan.hive_ids || [];
          // If hive_numbers populated, use them
          if (numbers.length > 0) {
            return numbers.map((num, i) => ({
              hive_number: num,
              hive_id: ids[i],
              treatment_name: plan.treatment_type_name,
              status: plan.status,
              yard_name: yardOfHive(ids[i], num) || plan.yard_name || ''
            }));
          }
          // Fallback: resolve from hive_ids using the hives prop
          if (ids.length > 0) {
            return ids.map((hid) => {
              const h = hives.find(hv => hv.id === hid);
              return {
                hive_number: h ? (h.nickname || h.hive_number) : '?',
                hive_id: hid,
                treatment_name: plan.treatment_type_name,
                status: plan.status,
                yard_name: h?.yard || plan.yard_name || ''
              };
            });
          }
          // Plan has no hives associated — show the plan itself
          return [{
            hive_number: 'All hives',
            treatment_name: plan.treatment_type_name,
            status: plan.status,
            yard_name: plan.yard_name || ''
          }];
        })}
      />

      {/* Mite Load / Tested Hives Popup */}
      <HivePopup
        isOpen={showMiteLoadPopup}
        onClose={() => setShowMiteLoadPopup(false)}
        title="Recently Tested Hives"
        icon={Bug}
        iconColor={(miteSummary?.average_percentage || 0) >= 3 ? 'text-red-600' : 'text-green-600'}
        description={`Average mite load: ${miteSummary?.average_percentage?.toFixed(1) || 0}%`}
        appLogo={appSettings?.app_logo}
        hives={yardMiteHistory.slice(0, 10).map(count => ({
          hive_number: count.hive_number,
          hive_id: count.hive_id,
          yard_name: yardOfHive(count.hive_id, count.hive_number),
          percentage: count.percentage,
          date: count.date
        }))}
      />

      {/* High Risk Hives Popup */}
      <HivePopup
        isOpen={showHighRiskPopup}
        onClose={() => setShowHighRiskPopup(false)}
        title="High Risk Hives"
        icon={AlertTriangle}
        iconColor="text-red-600"
        description="Hives with mite load above 3% threshold - treatment recommended"
        appLogo={appSettings?.app_logo}
        hives={(miteSummary?.high_risk_hives || []).map(hive => ({
          hive_number: hive.hive_number,
          hive_id: hive.hive_id,
          yard_name: yardOfHive(hive.hive_id, hive.hive_number),
          percentage: hive.percentage,
          date: hive.date
        }))}
        activePlans={plans}
        onStartTreatment={(hiveNum, hiveId) => {
          onAddPlan?.({ hive_number: hiveNum, hive_id: hiveId });
        }}
      />

      {/* Notification Settings Dialog */}
      <Dialog open={showNotificationSettings} onOpenChange={setShowNotificationSettings}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-indigo-600" />
              Notification Settings
            </DialogTitle>
            <DialogDescription>
              Configure email reminders for treatment and hive management
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Treatment Reminders */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">Treatment Reminders</Label>
                <p className="text-sm text-stone-500">Get seasonal treatment reminders</p>
              </div>
              <Switch
                checked={notificationSettings?.email_treatment_reminders ?? true}
                onCheckedChange={(checked) => handleUpdateNotificationSettings('email_treatment_reminders', checked)}
              />
            </div>

            {/* Inspection Reminders */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">Inspection Reminders</Label>
                <p className="text-sm text-stone-500">Get notified when inspections are due</p>
              </div>
              <Switch
                checked={notificationSettings?.email_inspection_reminders ?? true}
                onCheckedChange={(checked) => handleUpdateNotificationSettings('email_inspection_reminders', checked)}
              />
            </div>

            {/* Mite Alerts */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">High Mite Alerts</Label>
                <p className="text-sm text-stone-500">Get alerted when mite levels are critical</p>
              </div>
              <Switch
                checked={notificationSettings?.email_mite_alerts ?? true}
                onCheckedChange={(checked) => handleUpdateNotificationSettings('email_mite_alerts', checked)}
              />
            </div>

            <QueenReminderSettings token={token} />

            {/* Test Email Button */}
            <div className="pt-4 border-t">
              <Button
                onClick={handleSendReminder}
                disabled={sendingReminder}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
              >
                {sendingReminder ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test Reminder Email
                  </>
                )}
              </Button>
              <p className="text-xs text-stone-500 text-center mt-2">
                This will send a seasonal treatment reminder to your email
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Post-Treatment Mite Count Dialog */}
      <Dialog open={showMiteCountDialog} onOpenChange={setShowMiteCountDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-amber-600" />
              Post-Treatment Mite Count
            </DialogTitle>
            <DialogDescription>
              Enter your mite wash results to see if the treatment was effective.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-stone-700 font-medium">Mites Found</Label>
              <Input
                type="number"
                min="0"
                placeholder="Number of mites"
                value={postMiteCount}
                onChange={(e) => setPostMiteCount(e.target.value)}
                className="mt-1"
                data-testid="post-mite-count-input"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-stone-700 font-medium">Bee Sample Size</Label>
              <Input
                type="number"
                min="1"
                placeholder="300"
                value={postBeeSample}
                onChange={(e) => setPostBeeSample(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-stone-400 mt-1">Standard half-cup = ~300 bees</p>
            </div>
            
            {postMiteCount && (
              <div className="rounded-lg p-3 border text-center">
                {(() => {
                  const pct = (parseInt(postMiteCount) / (parseInt(postBeeSample) || 300)) * 100;
                  const color = pct < 2 ? 'border-green-300 bg-green-50' : pct < 4 ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50';
                  const textColor = pct < 2 ? 'text-green-700' : pct < 4 ? 'text-amber-700' : 'text-red-700';
                  const label = pct < 2 ? 'Treatment Effective' : pct < 4 ? 'Moderate — Consider Re-treating' : 'High — Re-treatment Needed';
                  const icon = pct < 2 ? '✅' : pct < 4 ? '⚠️' : '🚨';
                  return (
                    <div className={`rounded-lg p-3 ${color}`}>
                      <div className={`text-2xl font-bold ${textColor}`}>{pct.toFixed(1)}%</div>
                      <div className={`text-sm font-medium ${textColor} mt-1`}>{icon} {label}</div>
                    </div>
                  );
                })()}
              </div>
            )}
            
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowMiteCountDialog(false)} className="flex-1">Cancel</Button>
              <Button 
                onClick={handleMiteCountSubmit} 
                disabled={!postMiteCount}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
              >
                Save & Complete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TreatmentPlansView;

// Rotation Planner Component
const CATEGORY_COLORS = {
  organic: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', dot: 'bg-green-500' },
  chemical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', dot: 'bg-red-500' },
  mechanical: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500' },
  cultural: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', dot: 'bg-purple-500' },
};

const RotationPlanner = ({ data }) => {
  const [showTimeline, setShowTimeline] = useState(false);
  
  if (!data) return null;
  
  const scoreColor = data.rotation_score >= 80 ? 'text-green-600' : data.rotation_score >= 50 ? 'text-amber-600' : 'text-red-600';
  const scoreBg = data.rotation_score >= 80 ? 'bg-green-50 border-green-200' : data.rotation_score >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
  const scoreRing = data.rotation_score >= 80 ? 'stroke-green-500' : data.rotation_score >= 50 ? 'stroke-amber-500' : 'stroke-red-500';
  
  return (
    <div className="bg-gradient-to-br from-stone-50 via-amber-50/30 to-orange-50/30 rounded-xl border border-amber-200 p-5" data-testid="rotation-planner">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-amber-600" />
          Treatment Rotation Planner
        </h3>
        {data.total_treatments > 0 && (
          <button 
            onClick={() => setShowTimeline(!showTimeline)} 
            className="text-sm text-amber-600 hover:text-amber-800 flex items-center gap-1"
            data-testid="toggle-rotation-timeline"
          >
            {showTimeline ? 'Hide' : 'Show'} History
            <ChevronDown className={`h-4 w-4 transition-transform ${showTimeline ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* Score + Recommendation Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Rotation Health Score */}
        <div className={`${scoreBg} border rounded-xl p-4 flex items-center gap-4`} data-testid="rotation-score">
          <div className="relative w-16 h-16 flex-shrink-0">
            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.5" fill="none" className={scoreRing} strokeWidth="3"
                strokeDasharray={`${data.rotation_score * 0.974} 100`}
                strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-lg font-bold ${scoreColor}`}>{data.rotation_score}</span>
            </div>
          </div>
          <div>
            <div className="text-sm font-bold text-stone-800">Rotation Health</div>
            <div className={`text-xs ${scoreColor} font-medium`}>
              {data.rotation_score >= 80 ? 'Excellent — good variety' : data.rotation_score >= 50 ? 'Fair — could improve' : 'Poor — rotate treatments!'}
            </div>
            <div className="text-xs text-stone-500 mt-1">
              {data.unique_types_used} type{data.unique_types_used !== 1 ? 's' : ''} used across {data.total_treatments} treatment{data.total_treatments !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Recommended Next Treatment */}
        {data.recommendation && (
          <div className="lg:col-span-2 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="rotation-recommendation">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-amber-600" />
              <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Recommended Next Treatment</span>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="font-bold text-stone-800 text-base">{data.recommendation.name}</div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {(() => { const c = CATEGORY_COLORS[data.recommendation.category] || CATEGORY_COLORS.organic; return (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>{data.recommendation.category}</span>
                  ); })()}
                  {data.recommendation.efficacy && (
                    <span className="text-xs text-green-600 font-medium">{data.recommendation.efficacy} efficacy</span>
                  )}
                  {data.recommendation.resistance_risk === 'none' && (
                    <span className="text-xs text-emerald-600 flex items-center gap-0.5"><Shield className="h-3 w-3" /> No resistance risk</span>
                  )}
                </div>
                {data.recommendation.reason && (
                  <p className="text-xs text-stone-500 mt-1.5 italic">{data.recommendation.reason}</p>
                )}
              </div>
            </div>

            {/* Alternatives */}
            {data.alternatives?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <div className="text-xs text-amber-600 font-medium mb-1.5">Also consider:</div>
                <div className="flex flex-wrap gap-2">
                  {data.alternatives.map((alt, i) => {
                    const c = CATEGORY_COLORS[alt.category] || CATEGORY_COLORS.organic;
                    return (
                      <span key={i} className={`text-xs px-2 py-1 rounded-lg border ${c.border} ${c.bg} ${c.text}`}>
                        {alt.name}
                        {alt.efficacy && <span className="ml-1 opacity-70">({alt.efficacy})</span>}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Warnings */}
      {data.warnings?.length > 0 && (
        <div className="space-y-2 mb-4">
          {data.warnings.map((w, i) => (
            <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${
              w.severity === 'high' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
            }`} data-testid={`rotation-warning-${i}`}>
              {w.icon === 'rotate' ? (
                <RefreshCw className={`h-5 w-5 flex-shrink-0 ${w.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
              ) : w.icon === 'shield' ? (
                <Shield className={`h-5 w-5 flex-shrink-0 ${w.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
              ) : (
                <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${w.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
              )}
              <div>
                <div className={`text-sm font-bold ${w.severity === 'high' ? 'text-red-700' : 'text-amber-700'}`}>{w.title}</div>
                <p className={`text-xs mt-0.5 ${w.severity === 'high' ? 'text-red-600' : 'text-amber-600'}`}>{w.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Treatment Type Usage */}
      {data.type_usage?.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-medium text-stone-500 mb-2">Treatment Usage Distribution</div>
          <div className="flex flex-wrap gap-2">
            {data.type_usage.map((u, i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-lg px-3 py-1.5 text-xs flex items-center gap-2">
                <span className="font-medium text-stone-700">{u.name}</span>
                <span className="bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5 font-bold text-[10px]">{u.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {showTimeline && data.timeline?.length > 0 && (
        <div className="border-t border-amber-200 pt-4 mt-2">
          <div className="text-xs font-medium text-stone-500 mb-3">Treatment History Timeline</div>
          <div className="space-y-2">
            {data.timeline.slice().reverse().map((entry, i) => {
              const c = CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.organic;
              return (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${c.dot}`} />
                  <div className="w-24 text-stone-400 flex-shrink-0">
                    {entry.start_date ? new Date(entry.start_date).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }) : '—'}
                  </div>
                  <div className="flex-1 font-medium text-stone-700">{entry.type_name}</div>
                  <span className={`px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>{entry.category}</span>
                  {entry.resistance_risk && entry.resistance_risk !== 'none' && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      entry.resistance_risk === 'high' ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'
                    }`}>
                      {entry.resistance_risk === 'high' ? 'RESIST.' : 'MOD.'}
                    </span>
                  )}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                    entry.status === 'completed' ? 'bg-green-100 text-green-600' : entry.status === 'active' ? 'bg-blue-100 text-blue-600' : 'bg-stone-100 text-stone-500'
                  }`}>{entry.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.total_treatments === 0 && (
        <div className="text-center py-4 text-stone-500 text-sm">
          No treatments recorded yet. Start your first treatment to see rotation insights.
        </div>
      )}
    </div>
  );
};
